package ui.app.assign.detail.wheel.editor

import androidx.compose.runtime.*
import androidx.compose.ui.graphics.ImageBitmap
import common.frontend.api.canLayerBottomDelete
import common.frontend.api.canLayerBottomEdit
import io.Repository
import kotlinx.coroutines.launch
import org.ssoft.common.ui.wheel.AppWheelFactory
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.dialog__action_saved
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.await
import ui.snackBarStateGlobal

@VM
class AppEditorWheelVM : NavigationViewModel() {

    private lateinit var scopeUi: AppEditorWheelScope

    var state by mutableDataState<AppEditorWheelScope.Wrapper>()
        private set

    var activePageIndex by mutableStateOf(0)

    var wheelImage by mutableStateOf<ImageBitmap?>(null)
        private set

    val resultGenerator by lazy {
        AppWheelFactory.createResultGenerator(
            loadSvg = {
                runCatching {
                    Repository.files.fetchIcon(it)
                }.getOrNull()
            },
            loadWebp = {
                runCatching {
                    Repository.files.fetchImage(it)
                }.getOrNull()
            }
        )
    }

    private val wheelGenerator by lazy {
        AppWheelFactory.createWheelGenerator(
            loadSvg = {
                runCatching {
                    Repository.files.fetchIcon(it)
                }.getOrNull()
            },
            loadWebp = {
                runCatching {
                    Repository.files.fetchImage(it)
                }.getOrNull()
            }
        )
    }

    sealed interface Action {
        data object RegeneratePreview : Action
        data object Save : Action
        companion object {


            @Composable
            fun rememberAction(vm: AppEditorWheelVM): (Action) -> Unit {
                val nav = LocalNavigation.current
                return remember {
                    { act ->
                        when (act) {
                            Save -> vm.save(nav)
                            RegeneratePreview -> {
                                vm.state.entry?.also {
                                    vm.regeneratePreview(it)
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    private fun regeneratePreview(wrapper: AppEditorWheelScope.Wrapper) {
        wheelGenerator.postTask(wrapper.appRef.content)
    }


    fun init(scopeUi: AppEditorWheelScope) {
        this.scopeUi = scopeUi
        if (state is DataState.Result) return

        scope.launch {
            runCatching {
                val app = Repository.api.app.single(scopeUi.args.companyId, scopeUi.args.appId)


                state = DataState.Result(
                    AppEditorWheelScope.Wrapper(
                        canEdit = scopeUi.login.canLayerBottomEdit(scopeUi.args.companyId),
                        canDelete = scopeUi.login.canLayerBottomDelete(scopeUi.args.companyId),
                        data = app
                    ) { nnvl ->
                        regeneratePreview(nnvl)
                    }
                )
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error("Connection fail ${it.message}")
            }.onSuccess {
                wheelGenerator.launchCollector(scopeUi.textResolver) {
                    wheelImage = it
                }
            }
        }

    }

    private fun save(nav: Navigation) {
        scope.launch {
            state.entry?.apply {
                runCatching {
                    Repository.api.app.edit(scopeUi.args.companyId, data)
                }.onFailure {
                    it.printStackTrace()
                    snackBarStateGlobal.showSnackbar(
                        message = it.message ?: "Fail to save",
                        withDismissAction = true
                    )
                }.onSuccess {
                    launch {
                        snackBarStateGlobal.showSnackbar(
                            message = Res.string.dialog__action_saved.await(),
                            withDismissAction = false
                        )
                    }
                    nav.up()
                }
            }
        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }
}
